import React, { useState } from 'react';
import { Form, FormRenderProps } from 'react-final-form';

import Button, { ButtonSizes, ButtonStyles, ButtonTypes } from '@Components/Buttons/Button';
import FormField from '@Components/forms/FormField';
import RangeInput from '@Components/forms/inputs/RangeInput';
import AdCategoryTypeSelectInput from '@Components/forms/inputs/selects/AdCategorySelect/AdCategorySelect';
import { AllCategoriesProps } from '@Components/forms/inputs/selects/CategorySelect';
import { TextInputProps } from '@Components/forms/inputs/TextInput';
import TextInput from '@Components/forms/inputs/TextInput/TextInput';
import Image from '@Components/Image';
import InfoMessage, { MessageTypes } from '@Components/InfoMessage';
import Row from '@Components/layout/Row';
import { useAppKindContext } from '@Components/root';
import Typography from '@Components/Typography';
import { RouteConfig } from '@Config/routes';
import { CategoryKind, GetMarketplaceAdsQueryVariables } from '@Graphql/graphqlTypes.generated';
import { useTranslations } from '@Hooks/useTranslations';
import ExcavatorImage from '@Images/excavator-landing.webp';
import TractorImage from '@Images/tractor.webp';
import { validateMinLength } from '@Utils/form';

import styles from './HeroSection.module.scss';

export type HeroSearchFormSubmitValues = Pick<GetMarketplaceAdsQueryVariables, 'categories' | 'searchBy'> & {
  priceRange?: {
    from?: string;
    to?: string;
  };
};

const renderForm = (props: FormRenderProps<HeroSearchFormSubmitValues>) => {
  const t = useTranslations();
  const { handleSubmit, form } = props;

  const [fieldKey, setFieldKey] = useState(0);

  return (
    <div className={styles.formWrapper}>
      <InfoMessage type={MessageTypes.error} message={props.submitError} />
      <div className={styles.formContainer}>
        <form onSubmit={handleSubmit} className={styles.form}>
          <FormField<TextInputProps<string>>
            name="searchBy"
            type="text"
            component={TextInput}
            placeholder={t('msg_landing_search_form_search_input')}
            validate={validateMinLength(t, 3)}
            bottomSpacing={false}
          />

          <FormField
            name="categories"
            component={AdCategoryTypeSelectInput}
            placeholder={t('msg_landing_search_form_categories_select_input')}
            serviceType={AllCategoriesProps.allCategories}
            key={`unique_select_key_${fieldKey}`}
            isMulti
            bottomSpacing={false}
            largeDropdown
          />

          <FormField
            name="priceRange"
            component={RangeInput}
            key={`unique_range_input_key_${fieldKey}`}
            bottomSpacing={false}
          />

          <Row>
            <div className={styles.buttonsContainer}>
              <Row className={styles.submitButtonContainer}>
                <Button
                  type={ButtonTypes.submit}
                  label={t('msg_btn_submit_filters')}
                  onClick={handleSubmit}
                  className={styles.submitButton}
                  size={ButtonSizes.m}
                />
              </Row>

              <Row justifyBetween className={styles.bottomButtonsContainer}>
                <Button
                  onClick={() => {
                    form.reset();
                    setFieldKey(prev => prev + 1);
                  }}
                  style={ButtonStyles.plainBold}
                  label={t('msg_landing_clear_filters_button')}
                  size={ButtonSizes.unset}
                />

                <Button
                  onClick={handleSubmit}
                  style={ButtonStyles.plainBold}
                  label={t('msg_landing_detailed_search_button')}
                  size={ButtonSizes.unset}
                />
              </Row>
            </div>
          </Row>
        </form>
      </div>
    </div>
  );
};

type HeroSectionProps = {
  onSubmit: (values: HeroSearchFormSubmitValues) => void;
};

const HeroSection = ({ onSubmit }: HeroSectionProps) => {
  const t = useTranslations();
  const { appKind } = useAppKindContext();

  const isAgro = appKind === CategoryKind.Agro;
  const appKindName = appKind.toLowerCase() as Lowercase<CategoryKind>;

  return (
    <div className={styles.heroSectionContainer}>
      <Form<HeroSearchFormSubmitValues> onSubmit={onSubmit} render={props => renderForm(props)} />
      <Image src={isAgro ? TractorImage : ExcavatorImage} alt="banner" className={styles.backgroundImage} />
      <Row column className={styles.textContainer}>
        <div className={styles.textBox}>
          <div className={styles.textWrapper}>
            <Typography
              className={styles.title}
              msg={t(`msg_landing_page_hero_title_${appKindName}`)}
              html
              tag="h3"
              color="white"
            />
            <Typography
              className={styles.text}
              msg={t(`msg_landing_page_hero_subtitle_${appKindName}`)}
              html
              tag="span"
              size="l"
              color="white"
            />

            {isAgro && (
              <a href={RouteConfig.RentInfo.buildLink()} className={styles.linkButton}>
                <Typography msg={t('msg_landing_page_hero_button')} tag="span" />
              </a>
            )}
          </div>
        </div>
      </Row>
    </div>
  );
};

export default HeroSection;
